/* =========================================================================
   AUTOMATIC UPDATE ON REDEPLOY

   The problem this solves: a browser that has cached index.html keeps loading
   the bundle that document names, which is the *old* bundle, forever. The
   assets are not the stale part — Vite fingerprints them, so a changed module
   always has a new filename. The stale part is always the document pointing at
   them. That is exactly why an incognito tab shows the new site: it has no
   cached document.

   So the page boots knowing its own build id (stamped into the HTML by
   scripts/stamp-build-version.mjs), and asks the server what the current one
   is. When they disagree, the site has been redeployed underneath this tab,
   and it reloads itself.

   WHAT CAN AND CANNOT BE CLEARED FROM JAVASCRIPT — worth being exact, because
   "clear all the cache" means different things:

     Cache Storage (service workers)  can be deleted. Done below.
     Service worker registrations     can be unregistered. Done below.
     The HTTP disk cache              CANNOT be emptied by a page. No API
                                      exists. What can be done is refetch the
                                      document with cache:'reload', which
                                      forces a network hit and replaces that
                                      entry, and that is what makes the reload
                                      pick up new HTML rather than the copy it
                                      already had.

   Everything else is handled by headers rather than script: .htaccess serves
   HTML and version.json no-store, and the fingerprinted assets immutable, so
   after the document is fresh nothing else can be stale.
   ========================================================================= */

const VERSION_URL = '/version.json'
const POLL_MS = 5 * 60 * 1000
const RELOAD_GUARD = 'sx:reloaded-for-build'

/** The build this tab is running, as stamped into its HTML at build time. */
function bootBuild() {
  return document.querySelector('meta[name="app-build"]')?.content?.trim() || ''
}

/**
 * Whether the visitor has typed something they would lose.
 *
 * A forced reload in the middle of the contact form, or of the phone
 * verification code, throws away work the visitor did and looks like a crash.
 * When that is the case the update is applied at the next navigation instead,
 * which is usually seconds away and costs them nothing.
 */
function hasUnsavedInput() {
  const fields = document.querySelectorAll('input, textarea, select')
  for (const el of fields) {
    if (el.type === 'hidden' || el.disabled || el.readOnly) continue
    if (el.type === 'checkbox' || el.type === 'radio') {
      if (el.checked !== el.defaultChecked) return true
      continue
    }
    if (typeof el.value === 'string' && el.value.trim() !== '') return true
  }
  return false
}

async function purgeCaches() {
  /* Any service worker would serve its own cached copies and outlive a
     reload, so it goes first. The site does not register one today; this is
     here so that a worker installed by a future change, or left behind by an
     earlier one, cannot pin a visitor to an old build. */
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})))
    }
  } catch { /* not supported, or blocked by privacy settings */ }

  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})))
    }
  } catch { /* Cache Storage unavailable */ }
}

async function applyUpdate(nextBuild) {
  /* Reload at most once per build. Without this, a proxy or an intermediary
     that keeps handing back the old document would put the tab in a reload
     loop — the version file says one thing, the document another, forever. */
  try {
    if (sessionStorage.getItem(RELOAD_GUARD) === nextBuild) return
    sessionStorage.setItem(RELOAD_GUARD, nextBuild)
  } catch { /* storage disabled; accept the small risk of one extra reload */ }

  await purgeCaches()

  /* Replace this document's cache entry with a fresh copy before reloading.
     location.reload() on its own is allowed to reuse the cached document,
     which would leave the tab on the same old build it started with. */
  try {
    await fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' })
  } catch { /* offline or blocked — the reload below still tries */ }

  window.location.reload()
}

async function check(force = false) {
  const boot = bootBuild()
  if (!boot) return // dev server, or an unstamped build: nothing to compare

  let data
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    })
    if (!res.ok) return
    data = await res.json()
  } catch {
    return // offline or the file is missing; try again on the next tick
  }

  const next = String(data?.build || '')
  if (!next || next === boot) return

  if (!force && hasUnsavedInput()) {
    pending = next
    return
  }
  await applyUpdate(next)
}

/* A newer build seen while the visitor was mid-form, applied once it is safe. */
let pending = ''

function flushPending() {
  if (pending && !hasUnsavedInput()) {
    const build = pending
    pending = ''
    applyUpdate(build)
  }
}

let started = false

/**
 * Start watching for redeploys. Safe to call more than once.
 *
 * Checks on load, whenever the tab is brought back to the foreground — which
 * is the common case on a phone, where a tab can sit backgrounded for days —
 * on every route change, and on a slow timer for a tab left open.
 */
export function startVersionWatch() {
  if (started || typeof window === 'undefined') return
  started = true

  /* Not on the first paint: a check competing with the page's own requests
     costs first-load time for something that matters seconds later. */
  setTimeout(() => check(), 4000)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check()
  })
  window.addEventListener('focus', () => check())

  /* A route change is the safe moment: whatever the visitor was filling in is
     already behind them. */
  window.addEventListener('popstate', () => { flushPending(); check() })

  setInterval(() => { flushPending(); check() }, POLL_MS)
}

/** Exposed for the router, which knows about navigations popstate does not. */
export function onRouteChange() {
  flushPending()
}

export default startVersionWatch
